import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { MatDialog } from '@angular/material/dialog';
import { CigpStaffService } from '../../cigp-staff/cigp-staff.service';
import { UsersService } from '../../users/users.service';
import { catchError, combineLatest, map, Observable, throwError } from 'rxjs';
import { showErrorDialog } from '../../shared/utils';
import {
  AuditTrailIbPrivateOpportunitiesDistributionApiItem,
  EnrichedAuditTrailIbPrivateOpportunitiesDistributionItem,
  IbPrivateOpportunitiesFilesPendingDistributionCountResponse
} from './autit-trail-ib-private-opportunities-distribution-item.model';
import { orderBy } from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class AuditTrailIbPrivateOpportunitiesDistributionService {
  private apiEndpoint = `${environment.apiEndpoint}/api/audit-trail/ib-private-opportunities-distribution`;

  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    private errorDialog: MatDialog,
    private cigpStaffService: CigpStaffService,
    private usersService: UsersService
  ) {}

  public triggerIbPrivateOpportunitiesDistribution$(): Observable<{ message: string }> {
    return this.http.post<{ message: string }>('api/triggerIbPrivateOpportunitiesDistribution', {}).pipe(
      catchError((error: any) => {
        this.logger.error(error);
        showErrorDialog(this.errorDialog, error);
        return throwError(() => error);
      })
    );
  }

  public countIbPrivateOpportunitiesFilesPendingDistribution$(): Observable<IbPrivateOpportunitiesFilesPendingDistributionCountResponse> {
    return this.http
      .get<IbPrivateOpportunitiesFilesPendingDistributionCountResponse>(
        'api/countIbPrivateOpportunitiesFilesPendingDistribution'
      )
      .pipe(
        catchError((error: any) => {
          this.logger.error(error);
          showErrorDialog(this.errorDialog, error);
          return throwError(() => error);
        })
      );
  }

  public getAuditTrailIbPrivateOpportunitiesDistributionItems$(
    startDate: Date,
    endDate: Date,
    company: string | null,
    portfolioName: string | null
  ): Observable<EnrichedAuditTrailIbPrivateOpportunitiesDistributionItem[]> {
    return combineLatest([
      this.http.get<AuditTrailIbPrivateOpportunitiesDistributionApiItem[]>(`${this.apiEndpoint}`, {
        params: this.computeQueryParams(startDate, endDate, company, portfolioName)
      }),
      this.cigpStaffService.getStaffsFromCache$(),
      this.usersService.getUsersFromCache$()
    ]).pipe(
      catchError((error: any) => {
        this.logger.error(error);
        showErrorDialog(this.errorDialog, error);
        return throwError(() => error);
      }),
      map(([apiItems, staffs, users]) => {
        return apiItems.map((item) => {
          return {
            ...item,
            clientsFailedCount: item.failedToClientsEmails.length,
            clientsSuccessCount: item.distributedToClientsEmails.length,
            distributedToCigpStaffs: orderBy(
              [
                ...item.distributedToCigpStaffsEmails
                  .map((email) => staffs.find((s) => s.email === email))
                  .filter((s) => s !== undefined)
                  .map((s) => ({ ...s, success: true })),
                ...item.failedToCigpStaffsEmails
                  .map((email) => staffs.find((s) => s.email === email))
                  .filter((s) => s !== undefined)
                  .map((s) => ({ ...s, success: false }))
              ],
              'displayName'
            ),
            distributedToClients: orderBy(
              [
                ...item.distributedToClientsEmails
                  .map((email) => users.find((u) => u.email === email))
                  .filter((s) => s !== undefined)
                  .map((u) => ({ ...u, success: true })),
                ...item.failedToClientsEmails
                  .map((email) => users.find((u) => u.email === email))
                  .filter((s) => s !== undefined)
                  .map((u) => ({ ...u, success: false }))
              ],
              'displayName'
            ),
            notes: item.notes || '-',
            staffsFailedCount: item.failedToCigpStaffsEmails.length,
            staffsSuccessCount: item.distributedToCigpStaffsEmails.length
          };
        });
      })
    );
  }

  private computeQueryParams(startDate: Date, endDate: Date, company: string | null, portfolioName: string | null) {
    const params: { [param: string]: string } = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };

    if (company) {
      params['company'] = company;
    }

    if (portfolioName) {
      params['statementPortfolioName'] = portfolioName;
    }

    return params;
  }
}
